import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'

export interface VirtualCardFormStatistics {
  // Card type picker counts — used to disable options when no eligible cards exist.
  // Each count is 0 when loading or undefined.
  // billPayCount is the total across all types; the rest are per-capability subsets.
  billPayCount: number
  standardCount: number
  recurringCount: number
  guestAccessCount: number
  balanceLockCount: number
  issuerCycleCount: number

  // Source-card subsets of the above — used to decide "cards" vs "accounts" labeling.
  billPaySourceCount: number
  standardSourceCount: number
  recurringSourceCount: number
  guestAccessSourceCount: number
  balanceLockSourceCount: number
  issuerCycleSourceCount: number

  // Create-only counts (always 0 for the update variant, never queried).
  recipientMeCount: number
  activeDelegateCount: number
  delegateCount: number
  sourceCount: number
  activeSourceCount: number

  // Loading state
  allLoaded: boolean
}

const SHARED_KEYS = [
  StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveStandardCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveIssuerCycleEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceStandardCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount,
  StatisticsKeys.CreditCardsActiveSourceIssuerCycleEnabledCanIssueVirtualCardsCount,
] as const

const CREATE_ONLY_KEYS = [
  StatisticsKeys.VirtualCardsRecipientMeCount,
  StatisticsKeys.CreditCardsActiveDelegateCount,
  StatisticsKeys.CreditCardsDelegateCount,
  StatisticsKeys.CreditCardsSourceCount,
  StatisticsKeys.CreditCardsActiveSourceCount,
] as const

const CREATE_KEYS = [...SHARED_KEYS, ...CREATE_ONLY_KEYS] as const

const UPDATE_KEYS = SHARED_KEYS

interface UseVirtualCardFormStatisticsOptions {
  variant: 'create' | 'update'
  enabled?: boolean
}

export const useVirtualCardFormStatistics = ({
  variant,
  enabled = true,
}: UseVirtualCardFormStatisticsOptions): VirtualCardFormStatistics => {
  const statisticsKeys = variant === 'create' ? CREATE_KEYS : UPDATE_KEYS

  const { results, anyLoading } = useQueryStatistics({
    statisticsKeys,
    enabled,
  })

  const getCount = (key: StatisticsKeys): number => {
    const result = results[key as keyof typeof results]
    return result?.data ?? 0
  }

  const createOnlyCount = (key: StatisticsKeys): number => {
    if (variant !== 'create') return 0
    return getCount(key)
  }

  return {
    billPayCount: getCount(
      StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
    ),
    standardCount: getCount(
      StatisticsKeys.CreditCardsActiveStandardCanIssueVirtualCardsCount,
    ),
    recurringCount: getCount(
      StatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount,
    ),
    guestAccessCount: getCount(
      StatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCount,
    ),
    balanceLockCount: getCount(
      StatisticsKeys.CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount,
    ),
    issuerCycleCount: getCount(
      StatisticsKeys.CreditCardsActiveIssuerCycleEnabledCanIssueVirtualCardsCount,
    ),
    billPaySourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCount,
    ),
    standardSourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceStandardCanIssueVirtualCardsCount,
    ),
    recurringSourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount,
    ),
    guestAccessSourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount,
    ),
    balanceLockSourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount,
    ),
    issuerCycleSourceCount: getCount(
      StatisticsKeys.CreditCardsActiveSourceIssuerCycleEnabledCanIssueVirtualCardsCount,
    ),
    recipientMeCount: createOnlyCount(
      StatisticsKeys.VirtualCardsRecipientMeCount,
    ),
    activeDelegateCount: createOnlyCount(
      StatisticsKeys.CreditCardsActiveDelegateCount,
    ),
    delegateCount: createOnlyCount(StatisticsKeys.CreditCardsDelegateCount),
    sourceCount: createOnlyCount(StatisticsKeys.CreditCardsSourceCount),
    activeSourceCount: createOnlyCount(
      StatisticsKeys.CreditCardsActiveSourceCount,
    ),
    allLoaded: enabled && !anyLoading,
  }
}
